import { build_dsar_row, build_pi_inventory_row, build_processing_row, create_dsar_form, create_pi_inventory_form, create_processing_form } from './data_privacy_builders';

function build_pi_inventory_form_state(row) {
  if (!row) {
    const empty_pi_inventory_form = create_pi_inventory_form();
    return empty_pi_inventory_form;
  }
  const pi_inventory_form_state = { item: row.item, subject: row.subject, category: row.category, system: row.system, source: row.source, linked_processing_activity: row.linked_processing_activity, owner: row.owner, sensitivity: row.sensitivity.label, status: row.status.label };
  return pi_inventory_form_state;
}

function build_processing_form_state(row) {
  if (!row) {
    const empty_processing_form = create_processing_form();
    return empty_processing_form;
  }
  const processing_form_state = { activity: row.activity, category: row.category, purpose: row.purpose, basis: row.basis, owner: row.owner, retention: row.retention, safeguards: row.safeguards, status: row.status.label };
  return processing_form_state;
}

function build_dsar_form_state(row) {
  if (!row) {
    const empty_dsar_form = create_dsar_form();
    return empty_dsar_form;
  }
  const dsar_form_state = { subject: row.subject, type: row.type, received: row.received, due: row.due, owner: row.owner, status: row.status.label };
  return dsar_form_state;
}

function existing_row_value(rows, index) {
  const row_value = Reflect.get(rows, index) || {};
  return row_value;
}

function normalized_text(value, fallback_value) {
  const text_value = String(value || '').trim() || fallback_value;
  return text_value;
}

function pi_inventory_identity(form) {
  const identity = {
    item: normalized_text(form.item, 'New personal information element'),
    subject: normalized_text(form.subject, 'Pending classification'),
    category: normalized_text(form.category, 'Pending classification'),
    system: normalized_text(form.system, 'System pending'),
    source: normalized_text(form.source, 'Source pending'),
    linked_processing_activity: normalized_text(form.linked_processing_activity, 'Processing activity pending'),
    owner: normalized_text(form.owner, 'Owner pending')
  };
  return identity;
}

function processing_identity(form) {
  const identity = {
    activity: normalized_text(form.activity, 'New processing activity'),
    category: normalized_text(form.category, 'Pending classification'),
    purpose: normalized_text(form.purpose, 'Purpose pending'),
    owner: normalized_text(form.owner, 'Owner pending'),
    retention: normalized_text(form.retention, 'Retention pending'),
    safeguards: normalized_text(form.safeguards, 'Safeguards pending')
  };
  return identity;
}

function dsar_identity(form) {
  const identity = {
    subject: normalized_text(form.subject, 'Unknown data subject'),
    received: normalized_text(form.received, 'Pending date'),
    due: normalized_text(form.due, 'Pending date'),
    owner: normalized_text(form.owner, 'Owner pending')
  };
  return identity;
}

function next_sequence_ref(context, sequence_key, prefix) {
  const current_value = Number(Reflect.get(context, sequence_key) || 0) + 1;
  Reflect.set(context, sequence_key, current_value);
  const next_ref = `${prefix}-${String(current_value).padStart(2, '0')}`;
  return next_ref;
}

function normalized_pi_inventory_row(form, ref, connected_records) {
  const identity = pi_inventory_identity(form);
  const pi_inventory_row = {
    ref,
    ...identity,
    sensitivity_label: form.sensitivity,
    status_label: form.status,
    connected_records
  };
  return pi_inventory_row;
}

function build_pi_inventory_submission(context) {
  const form = context.pi_inventory_form;
  const is_new = context.pi_inventory_edit_index === null;
  const existing_row = existing_row_value(context.pi_inventory_rows, context.pi_inventory_edit_index);
  const ref = is_new ? next_sequence_ref(context, 'pi_inventory_sequence', 'PI-INV') : existing_row.ref;
  const existing_connected_records = is_new ? [] : existing_row.connected_records || [];
  const pi_inventory_submission = {
    ref,
    row: build_pi_inventory_row(normalized_pi_inventory_row(form, ref, existing_connected_records)),
    is_new
  };
  return pi_inventory_submission;
}

function normalized_processing_row(form, ref, connected_records) {
  const identity = processing_identity(form);
  const processing_row = {
    ref,
    activity: identity.activity,
    category: identity.category,
    purpose: identity.purpose,
    basis: form.basis,
    owner: identity.owner,
    retention: identity.retention,
    safeguards: identity.safeguards,
    status_label: form.status,
    connected_records
  };
  return processing_row;
}

function build_processing_submission(context) {
  const form = context.processing_form;
  const is_new = context.processing_edit_index === null;
  const existing_row = existing_row_value(context.processing_rows, context.processing_edit_index);
  const ref = is_new ? next_sequence_ref(context, 'processing_sequence', 'PA') : existing_row.ref;
  const existing_connected_records = is_new ? [] : existing_row.connected_records || [];
  const processing_submission = {
    ref,
    row: build_processing_row(normalized_processing_row(form, ref, existing_connected_records)),
    is_new
  };
  return processing_submission;
}

function normalized_dsar_row(form, ref) {
  const identity = dsar_identity(form);
  const dsar_row = {
    ref,
    subject: identity.subject,
    type: form.type,
    received: identity.received,
    due: identity.due,
    owner: identity.owner,
    status_label: form.status
  };
  return dsar_row;
}

function build_dsar_submission(context) {
  const form = context.dsar_form;
  const is_new = context.dsar_edit_index === null;
  const existing_row = existing_row_value(context.dsar_rows, context.dsar_edit_index);
  const ref = is_new ? next_sequence_ref(context, 'dsar_sequence', 'DSR') : existing_row.ref;
  const dsar_submission = {
    row: build_dsar_row(normalized_dsar_row(form, ref)),
    is_new
  };
  return dsar_submission;
}

function apply_submission(vm, rows_key, edit_index_key, submission) {
  const target_rows = Reflect.get(vm, rows_key);
  const edit_index = Reflect.get(vm, edit_index_key);
  if (submission.is_new) {
    target_rows.unshift(submission.row);
    return;
  }
  vm.$set(target_rows, edit_index, submission.row);
}

export const data_privacy_modal_methods = {
  open_pi_inventory_modal(row = null, index = null) {
    this.close_pi_inventory_drawer();
    const edit_index = typeof index === 'number' ? index : null;
    this.pi_inventory_edit_index = edit_index;
    this.pi_inventory_form = build_pi_inventory_form_state(row);
    this.pi_inventory_modal_open = true;
    this.sync_overlay_state();
  },
  close_pi_inventory_modal() {
    this.pi_inventory_modal_open = false;
    this.pi_inventory_edit_index = null;
    this.pi_inventory_form = create_pi_inventory_form();
    this.sync_overlay_state();
  },
  submit_pi_inventory_modal() {
    const submission = build_pi_inventory_submission(this);
    apply_submission(this, 'pi_inventory_rows', 'pi_inventory_edit_index', submission);
    this.close_pi_inventory_modal();
    this.open_pi_inventory_drawer(submission.ref);
  },
  open_processing_modal(row = null, index = null) {
    this.close_processing_drawer();
    const edit_index = typeof index === 'number' ? index : null;
    this.processing_edit_index = edit_index;
    this.processing_form = build_processing_form_state(row);
    this.processing_modal_open = true;
    this.sync_overlay_state();
  },
  close_processing_modal() {
    this.processing_modal_open = false;
    this.processing_edit_index = null;
    this.processing_form = create_processing_form();
    this.sync_overlay_state();
  },
  submit_processing_modal() {
    const submission = build_processing_submission(this);
    apply_submission(this, 'processing_rows', 'processing_edit_index', submission);
    this.close_processing_modal();
    this.open_processing_drawer(submission.ref);
  },
  open_dsar_modal(row = null, index = null) {
    const edit_index = typeof index === 'number' ? index : null;
    this.dsar_edit_index = edit_index;
    this.dsar_form = build_dsar_form_state(row);
    this.dsar_modal_open = true;
    this.sync_overlay_state();
  },
  submit_dsar_modal() {
    const submission = build_dsar_submission(this);
    apply_submission(this, 'dsar_rows', 'dsar_edit_index', submission);
    this.close_dsar_modal();
  }
};
